import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, athleteClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { el as elLocale } from "date-fns/locale";
import {
  Loader2,
  LogOut,
  CalendarDays,
  Users,
  TrendingUp,
  CheckCircle2,
  Dumbbell,
  Lock,
  User,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { UpcomingCourtBookings } from "@/components/UpcomingCourtBookings";
import { useT } from "@/lib/i18n";

import { useSeo } from "@/lib/seo";
export default function TrainingPage() {
  useSeo({ title: "Training Sessions — Beach Volleyball Chania", description: "Book your beach volleyball training session at The Sand Lab academy in Chania, Greece. Silver, Golden and Open sessions every week.", path: "/training" });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");

  useEffect(() => {
    const token = localStorage.getItem("sandlab_athlete_token");
    if (!token) {
      setLoading(false);
      return;
    }
    athleteClient
      .get("/training/me")
      .then((r) => setUser(r.data))
      .catch(() => {
        localStorage.removeItem("sandlab_athlete_token");
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-ocean" />
      </div>
    );
  }

  if (inviteToken && !user) {
    return <AthleteRegister token={inviteToken} onAuth={setUser} />;
  }

  if (!user) {
    return <AthleteLogin onAuth={setUser} />;
  }

  return (
    <AthleteDashboard
      user={user}
      onLogout={() => {
        localStorage.removeItem("sandlab_athlete_token");
        setUser(null);
      }}
    />
  );
}

// ============ Athlete Profile Dialog ============
function AthleteProfileDialog({ open, onOpenChange, user }) {
  const { t } = useT();
  const [changing, setChanging] = useState(false);
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [showPwForm, setShowPwForm] = useState(false);

  const reset = () => {
    setForm({ current_password: "", new_password: "", confirm_password: "" });
    setShowPwForm(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm_password) {
      toast.error(t("train_password_mismatch"));
      return;
    }
    setChanging(true);
    try {
      await athleteClient.post("/training/athlete/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success(t("train_password_changed"));
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setChanging(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent data-testid="athlete-profile-dialog" className="sm:max-w-md rounded-3xl">
        <DialogHeader>
          <div className="mx-auto h-14 w-14 rounded-full bg-ocean/10 flex items-center justify-center mb-2">
            <User className="h-7 w-7 text-ocean" />
          </div>
          <DialogTitle className="text-center text-2xl font-heading font-black">
            {t("train_profile_title")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-2 text-sm">
          <InfoRow label={t("train_name")} value={user.name} />
          <InfoRow label={t("train_surname")} value={user.surname} />
          <InfoRow label={t("train_username")} value={user.username} />
          <InfoRow label={t("train_email")} value={user.email} />
        </div>

        {!showPwForm ? (
          <Button
            type="button"
            onClick={() => setShowPwForm(true)}
            data-testid="open-change-password-btn"
            className="w-full h-11 rounded-full bg-sunset hover:bg-sunset-hover text-white font-bold mt-4"
          >
            <Lock className="h-4 w-4 mr-2" />
            {t("train_change_password")}
          </Button>
        ) : (
          <form onSubmit={submit} className="space-y-3 mt-4 border-t pt-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                {t("train_current_password")}
              </Label>
              <Input
                type="password"
                data-testid="current-password-input"
                value={form.current_password}
                onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                required
                className="mt-1.5 h-11 rounded-2xl"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                {t("train_new_password")}
              </Label>
              <Input
                type="password"
                data-testid="new-password-input"
                value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                required
                minLength={6}
                className="mt-1.5 h-11 rounded-2xl"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                {t("train_new_password")} (×2)
              </Label>
              <Input
                type="password"
                data-testid="confirm-password-input"
                value={form.confirm_password}
                onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                required
                minLength={6}
                className="mt-1.5 h-11 rounded-2xl"
              />
            </div>
            <Button
              type="submit"
              data-testid="submit-change-password-btn"
              disabled={changing}
              className="w-full h-11 rounded-full bg-sunset hover:bg-sunset-hover text-white font-bold"
            >
              {changing ? <Loader2 className="h-4 w-4 animate-spin" /> : t("train_save")}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

const InfoRow = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 bg-slate-50 rounded-2xl px-4 py-3 border border-slate-100">
    <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
      {label}
    </span>
    <span className="text-slate-900 font-semibold text-sm truncate" title={value}>
      {value}
    </span>
  </div>
);

// ============ Total Breakdown Dialog ============
function TotalBreakdownDialog({ open, onOpenChange, stats }) {
  const { t, lang } = useT();
  const months = stats?.by_month || [];
  const total = stats?.total_bookings ?? 0;
  const dateLocale = lang === "el" ? elLocale : undefined;

  const monthLabel = (key) => {
    // key = "YYYY-MM"
    const [y, m] = key.split("-").map(Number);
    return format(new Date(y, m - 1, 1), "LLLL yyyy", { locale: dateLocale });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="total-breakdown-dialog"
        className="sm:max-w-md rounded-3xl"
      >
        <DialogHeader>
          <div className="mx-auto h-14 w-14 rounded-full bg-ocean/10 flex items-center justify-center mb-2">
            <CheckCircle2 className="h-7 w-7 text-ocean" />
          </div>
          <DialogTitle className="text-center text-2xl font-heading font-black">
            {t("train_total")}: {total} {t("train_sessions")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t("train_breakdown_subtitle")}
          </DialogDescription>
        </DialogHeader>

        {months.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm">
            {t("train_no_history")}
          </div>
        ) : (
          <div className="space-y-2 mt-2">
            {months.map((m) => (
              <div
                key={m.month}
                data-testid={`breakdown-row-${m.month}`}
                className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-2xl px-4 py-3"
              >
                <span className="text-sm font-semibold text-slate-900 capitalize">
                  {monthLabel(m.month)}
                </span>
                <span className="text-base font-mono font-black text-ocean">
                  {m.count}
                </span>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============ Login ============
function AthleteLogin({ onAuth }) {
  const { t } = useT();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await apiClient.post("/training/athlete/login", form);
      localStorage.setItem("sandlab_athlete_token", data.token);
      onAuth(data.user);
      toast.success(`Welcome, ${data.user.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sand/40">
      <Navbar />
      <div className="pt-32 pb-20 px-6 flex justify-center">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-soft border border-slate-100 p-8 sm:p-10">
          <div className="flex justify-center mb-4">
            <div className="h-14 w-14 rounded-2xl bg-ocean text-white flex items-center justify-center">
              <Dumbbell className="h-7 w-7" />
            </div>
          </div>
          <h1 className="font-heading font-black text-3xl text-slate-900 text-center">
            {t("train_login_title")}
          </h1>
          <p className="text-slate-500 text-center mt-2 text-sm">
            {t("train_login_subtitle")}
          </p>

          <form onSubmit={submit} data-testid="athlete-login-form" className="mt-8 space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                {t("train_username")}
              </Label>
              <Input
                data-testid="athlete-username-input"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                placeholder="yannis"
                required
                className="mt-1.5 h-12 rounded-2xl border-slate-200 focus-visible:ring-ocean/30 focus-visible:border-ocean"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                {t("train_password")}
              </Label>
              <Input
                type="password"
                data-testid="athlete-password-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                className="mt-1.5 h-12 rounded-2xl border-slate-200 focus-visible:ring-ocean/30 focus-visible:border-ocean"
              />
            </div>
            <Button
              type="submit"
              data-testid="athlete-login-btn"
              disabled={loading}
              className="w-full h-12 rounded-full bg-ocean hover:bg-ocean-hover text-white font-bold"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> ...
                </>
              ) : (
                t("train_signin")
              )}
            </Button>
          </form>

          <p className="text-center text-xs text-slate-500 mt-6">
            {t("train_no_account")}{" "}
            <button
              onClick={() => navigate("/academy")}
              className="text-ocean font-semibold hover:underline"
            >
              ←
            </button>
          </p>

          <div className="text-center mt-3">
            <button
              type="button"
              onClick={() => setForgotOpen(true)}
              data-testid="forgot-password-link"
              className="text-xs text-sunset font-semibold hover:underline"
            >
              {t("train_forgot")}
            </button>
          </div>
        </div>
      </div>

      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} />
    </div>
  );
}

function ForgotPasswordDialog({ open, onOpenChange }) {
  const { t } = useT();
  const [form, setForm] = useState({ username: "", email: "", new_password: "" });
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setForm({ username: "", email: "", new_password: "" });
    setLoading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.post("/training/athlete/forgot-password", {
        username: form.username.trim().toLowerCase(),
        email: form.email.trim().toLowerCase(),
        new_password: form.new_password,
      });
      toast.success(t("train_reset_success"));
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("train_reset_no_match"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent data-testid="forgot-dialog" className="sm:max-w-md rounded-3xl">
        <DialogHeader>
          <div className="mx-auto h-14 w-14 rounded-full bg-sunset/10 flex items-center justify-center mb-2">
            <Lock className="h-7 w-7 text-sunset" />
          </div>
          <DialogTitle className="text-center text-2xl font-heading font-black">
            {t("train_reset_title")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t("train_reset_subtitle")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t("train_username")}
            </Label>
            <Input
              data-testid="forgot-username-input"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              required
              autoFocus
              className="mt-1.5 h-12 rounded-2xl border-slate-200 focus-visible:ring-ocean/30 focus-visible:border-ocean"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t("train_email")}
            </Label>
            <Input
              type="email"
              data-testid="forgot-email-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="mt-1.5 h-12 rounded-2xl border-slate-200 focus-visible:ring-ocean/30 focus-visible:border-ocean"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              {t("train_new_password")}
            </Label>
            <Input
              type="password"
              data-testid="forgot-newpw-input"
              value={form.new_password}
              onChange={(e) => setForm({ ...form, new_password: e.target.value })}
              required
              minLength={6}
              className="mt-1.5 h-12 rounded-2xl border-slate-200 focus-visible:ring-ocean/30 focus-visible:border-ocean"
            />
          </div>
          <Button
            type="submit"
            data-testid="forgot-submit-btn"
            disabled={loading}
            className="w-full h-12 rounded-full bg-sunset hover:bg-sunset-hover text-white font-bold"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("train_reset_btn")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============ Register via invite ============
function AthleteRegister({ token, onAuth }) {
  const { t } = useT();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    surname: "",
    username: "",
    email: "",
    password: "",
  });
  const [valid, setValid] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiClient
      .get("/training/invite-info", { params: { token } })
      .then(() => setValid(true))
      .catch(() => setValid(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await apiClient.post("/training/register", {
        token,
        ...form,
      });
      localStorage.setItem("sandlab_athlete_token", data.token);
      onAuth(data.user);
      toast.success(`Welcome, ${data.user.name}`);
      navigate("/training", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  if (valid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-ocean" />
      </div>
    );
  }

  if (valid === false) {
    return (
      <div className="min-h-screen bg-sand/40">
        <Navbar />
        <div className="pt-32 px-6 text-center">
          <p className="text-red-600 font-semibold">{t("train_invalid_invite")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand/40">
      <Navbar />
      <div className="pt-32 pb-20 px-6 flex justify-center">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-soft border border-slate-100 p-8 sm:p-10">
          <h1 className="font-heading font-black text-2xl text-slate-900 text-center">
            {t("train_register_title")}
          </h1>
          <p className="text-slate-500 text-center mt-2 text-sm">
            {t("train_register_subtitle")}
          </p>
          <form
            onSubmit={submit}
            data-testid="athlete-register-form"
            className="mt-6 space-y-3"
          >
            <div className="grid grid-cols-2 gap-3">
              <Input
                data-testid="reg-name"
                placeholder={t("train_name")}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                className="h-12 rounded-2xl"
              />
              <Input
                data-testid="reg-surname"
                placeholder={t("train_surname")}
                value={form.surname}
                onChange={(e) => setForm({ ...form, surname: e.target.value })}
                required
                className="h-12 rounded-2xl"
              />
            </div>
            <Input
              data-testid="reg-username"
              placeholder={t("train_username")}
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              required
              minLength={3}
              className="h-12 rounded-2xl"
            />
            <Input
              data-testid="reg-email"
              type="email"
              placeholder={t("train_email")}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="h-12 rounded-2xl"
            />
            <Input
              data-testid="reg-password"
              type="password"
              placeholder={t("train_password")}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={6}
              className="h-12 rounded-2xl"
            />
            <Button
              type="submit"
              data-testid="reg-submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-sunset hover:bg-sunset-hover text-white font-bold mt-2"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t("train_create_account")
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ============ Athlete Dashboard ============
function AthleteDashboard({ user, onLogout }) {
  const { t, lang } = useT();
  const dateLocale = lang === "el" ? elLocale : undefined;
  const [slots, setSlots] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileOpen, setProfileOpen] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const focusSlot = searchParams.get("slot");

  useEffect(() => {
    if (!focusSlot || loading) return;
    const el = document.querySelector(`[data-testid="train-slot-${focusSlot}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusSlot, loading]);

  const fetchAll = async () => {
    try {
      const [s, b, st] = await Promise.all([
        athleteClient.get("/training/slots"),
        athleteClient.get("/training/my-bookings"),
        athleteClient.get("/training/my-stats"),
      ]);
      setSlots(s.data);
      setMyBookings(b.data);
      setStats(st.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired");
        onLogout();
        return;
      }
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const book = async (slot_id) => {
    try {
      await athleteClient.post("/training/bookings", { slot_id });
      toast.success(t("train_booked"));
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    }
  };

  const cancel = async (booking_id) => {
    try {
      await athleteClient.delete(`/training/bookings/${booking_id}`);
      toast.success(t("train_cancel"));
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed");
    }
  };

  const joinWaitlist = async (slot_id) => {
    try {
      await athleteClient.post("/training/waitlists", { slot_id });
      toast.success(t("train_waitlist_joined"));
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const leaveWaitlist = async (entry_id) => {
    try {
      await athleteClient.delete(`/training/waitlists/${entry_id}`);
      toast.success(t("train_waitlist_left"));
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  // Group slots by week (Mon as anchor) and within week by date
  const groupedByWeek = {};
  slots.forEach((s) => {
    const d = parseISO(s.date);
    const monday = new Date(d);
    monday.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const weekKey = monday.toISOString().slice(0, 10);
    if (!groupedByWeek[weekKey]) groupedByWeek[weekKey] = {};
    if (!groupedByWeek[weekKey][s.date]) groupedByWeek[weekKey][s.date] = [];
    groupedByWeek[weekKey][s.date].push(s);
  });
  const weekKeys = Object.keys(groupedByWeek).sort();

  return (
    <div data-testid="athlete-dashboard" className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="pt-28 pb-20 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-sunset font-semibold mb-2">
              {t("train_nav")} · {user.name} {user.surname}
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl text-slate-900">
              {t("train_slots_title")}
            </h1>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setProfileOpen(true)}
              data-testid="athlete-profile-btn"
              variant="outline"
              className="rounded-full border-ocean text-ocean hover:bg-ocean/10"
            >
              <User className="h-4 w-4 mr-2" />
              {t("train_profile")}
            </Button>
            <Button
              onClick={onLogout}
              data-testid="athlete-logout-btn"
              className="rounded-full bg-slate-900 hover:bg-slate-800 text-white"
            >
              <LogOut className="h-4 w-4 mr-2" />
              {t("train_logout")}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          <StatCard
            label={t("train_my_month")}
            value={loading ? "—" : stats?.sessions_this_month ?? 0}
            sub={t("train_sessions")}
            icon={<TrendingUp className="h-5 w-5" />}
            color="bg-sunset"
          />
          <StatCard
            label={t("train_total")}
            value={loading ? "—" : stats?.total_bookings ?? 0}
            sub={t("train_sessions")}
            icon={<CheckCircle2 className="h-5 w-5" />}
            color="bg-ocean"
            onClick={() => setBreakdownOpen(true)}
            testid="athlete-total-stat-btn"
          />
          <StatCard
            label={t("train_my_bookings")}
            value={loading ? "—" : myBookings.length}
            sub={t("train_sessions")}
            icon={<CalendarDays className="h-5 w-5" />}
            color="bg-emerald-600"
          />
        </div>

        <UpcomingCourtBookings />

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-ocean" />
          </div>
        ) : weekKeys.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            {t("coach_slots_empty")}
          </div>
        ) : (
          <div className="space-y-8">
            {weekKeys.map((wk) => {
              const week = groupedByWeek[wk];
              const dayDates = Object.keys(week).sort();
              return (
                <div key={wk}>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">
                    {format(parseISO(wk), "MMM d", { locale: dateLocale })} —{" "}
                    {format(parseISO(dayDates[dayDates.length - 1]), "MMM d", {
                      locale: dateLocale,
                    })}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {dayDates.map((date) => (
                      <div
                        key={date}
                        className="bg-white rounded-3xl shadow-soft border border-slate-100 p-4"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <CalendarDays className="h-4 w-4 text-ocean" />
                          <h3 className="font-heading font-bold text-base text-slate-900">
                            {format(parseISO(date), "EEEE", {
                              locale: dateLocale,
                            })}
                          </h3>
                          <span className="text-xs text-slate-500 ml-auto">
                            {format(parseISO(date), "MMM d", {
                              locale: dateLocale,
                            })}
                          </span>
                        </div>
                        <div className="space-y-2">
                          {week[date].map((s) => (
                            <SlotCard
                              key={s.id}
                              slot={s}
                              t={t}
                              highlight={focusSlot === s.id}
                              onBook={() => book(s.id)}
                              onCancel={() => cancel(s.my_booking_id)}
                              onJoinWaitlist={() => joinWaitlist(s.id)}
                              onLeaveWaitlist={() => leaveWaitlist(s.my_waitlist_id)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <AthleteProfileDialog open={profileOpen} onOpenChange={setProfileOpen} user={user} />
      <TotalBreakdownDialog
        open={breakdownOpen}
        onOpenChange={setBreakdownOpen}
        stats={stats}
      />
    </div>
  );
}

const StatCard = ({ label, value, sub, icon, color, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    data-testid={testid}
    className={`text-left w-full bg-white rounded-3xl shadow-soft border border-slate-100 p-5 sm:p-6 transition-all ${
      onClick ? "hover:shadow-lg hover:-translate-y-0.5 cursor-pointer active:scale-[0.99]" : "cursor-default"
    }`}
  >
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
        {label}
      </span>
      <span
        className={`h-9 w-9 rounded-xl ${color} text-white flex items-center justify-center`}
      >
        {icon}
      </span>
    </div>
    <div className="mt-3 text-3xl font-heading font-black text-slate-900">{value}</div>
    <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
  </button>
);

const SlotCard = ({ slot, t, highlight, onBook, onCancel, onJoinWaitlist, onLeaveWaitlist }) => {
  const pct = slot.booked_count / slot.capacity;
  const barColor =
    pct >= 1 ? "bg-red-500" : pct >= 0.75 ? "bg-sunset" : "bg-emerald-500";
  return (
    <div
      data-testid={`train-slot-${slot.id}`}
      className={`rounded-2xl border border-slate-100 bg-slate-50/60 p-3 hover:bg-white transition-colors ${slot.locked ? "opacity-70" : ""} ${highlight ? "ring-4 ring-sunset/70 animate-pulse bg-white" : ""}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono font-bold text-sm text-ocean flex items-center gap-1.5">
          {slot.start_time}
          <span className="text-slate-400 text-xs"> → {slot.end_time}</span>
          {slot.level && (
            <Badge
              data-testid={`train-slot-level-${slot.id}`}
              className={`text-[9px] uppercase tracking-widest px-1.5 py-0 h-4 ${
                slot.level === "golden"
                  ? "bg-amber-100 text-amber-800 border border-amber-300"
                  : slot.level === "open"
                  ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                  : "bg-slate-200 text-slate-700 border border-slate-300"
              }`}
            >
              {slot.level}
            </Badge>
          )}
        </span>
        <Badge
          variant="outline"
          className="text-[10px] font-mono px-1.5 py-0 h-5"
        >
          {slot.booked_count}/{slot.capacity}
        </Badge>
      </div>
      <div className="h-1 bg-slate-200 rounded-full overflow-hidden mb-2">
        <div
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${Math.min(100, pct * 100)}%` }}
        />
      </div>

      {/* Athletes list */}
      {slot.athletes && slot.athletes.length > 0 && (
        <ul className="space-y-0.5 mb-2">
          {slot.athletes.map((a) => (
            <li
              key={a.user_id}
              className="text-[11px] text-slate-700 truncate"
              title={`${a.name} ${a.surname}`}
            >
              · {a.name} {a.surname}
            </li>
          ))}
        </ul>
      )}

      {/* Waitlist indicator */}
      {slot.waitlist_count > 0 && (
        <div className="text-[10px] text-slate-500 mb-1.5 flex items-center gap-1">
          <Users className="h-3 w-3" />
          {t("train_waitlist_position")}: {slot.waitlist_count}
        </div>
      )}

      {slot.is_past ? (
        <Badge variant="outline" className="text-[10px] text-slate-500 w-full justify-center">
          {t("train_past")}
        </Badge>
      ) : slot.locked ? (
        <Button
          size="sm"
          disabled
          data-testid={`train-locked-${slot.id}`}
          className="rounded-full bg-slate-200 text-slate-500 w-full h-8 text-xs disabled:opacity-100 cursor-not-allowed"
        >
          <Lock className="h-3 w-3 mr-1" />
          {t("train_locked_level")}
        </Button>
      ) : slot.my_booking_id ? (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              size="sm"
              data-testid={`train-cancel-${slot.id}`}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white w-full h-8 text-xs"
            >
              {t("train_cancel")}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-3xl">
            <AlertDialogHeader>
              <AlertDialogTitle>{t("train_cancel")}?</AlertDialogTitle>
              <AlertDialogDescription>
                {t("train_cancel_rule")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-full">×</AlertDialogCancel>
              <AlertDialogAction
                onClick={onCancel}
                className="rounded-full bg-red-600 hover:bg-red-700"
              >
                ✓
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : slot.my_waitlist_id ? (
        <Button
          size="sm"
          onClick={onLeaveWaitlist}
          data-testid={`train-leave-wl-${slot.id}`}
          variant="outline"
          className="rounded-full w-full h-8 text-xs border-sunset text-sunset hover:bg-sunset/10"
        >
          {t("train_on_waitlist")} · ✕
        </Button>
      ) : slot.is_full ? (
        <Button
          size="sm"
          onClick={onJoinWaitlist}
          data-testid={`train-join-wl-${slot.id}`}
          className="rounded-full bg-slate-700 hover:bg-slate-800 text-white w-full h-8 text-xs"
        >
          {t("train_join_waitlist")}
        </Button>
      ) : (
        <Button
          size="sm"
          onClick={onBook}
          data-testid={`train-book-${slot.id}`}
          className="rounded-full bg-sunset hover:bg-sunset-hover text-white w-full h-8 text-xs"
        >
          {t("train_book")}
        </Button>
      )}
    </div>
  );
};
